import { useEffect, useState } from 'react'
import {
  Rocket, CalendarDays, TrendingUp,
  Scissors, DollarSign, ArrowUpRight, ArrowDownRight,
  Trophy, AlertCircle,
} from 'lucide-react'
import { useIPO } from '@/hooks/useIPO'
import type { StockSplit } from '@/lib/types'
import { clsx } from 'clsx'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00')
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

function ReturnBadge({ value }: { value: number | null }) {
  if (value == null) return <span className="text-text-muted text-xs">N/A</span>
  const pos = value >= 0
  return (
    <span className={clsx(pos ? 'delta-chip-pos' : 'delta-chip-neg')}>
      {pos ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
      {pos ? '+' : ''}{value.toFixed(1)}%
    </span>
  )
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  const colours: Record<string, string> = {
    priced: 'bg-green/15 text-green',
    filed: 'bg-blue/15 text-blue-light',
    expected: 'bg-amber-400/15 text-amber-400',
    rumored: 'bg-text-muted/15 text-text-muted',
    withdrawn: 'bg-rose/15 text-rose-light',
  }
  const key = (status ?? '').toLowerCase()
  return (
    <span className={clsx('px-2 py-0.5 rounded text-xs font-medium capitalize', colours[key] ?? 'bg-text-muted/15 text-text-muted')}>
      {status}
    </span>
  )
}

// ── Tab: Upcoming IPOs ────────────────────────────────────────────────────────

function UpcomingTab() {
  const { upcoming, upcomingLoading, fetchUpcoming } = useIPO()

  useEffect(() => { fetchUpcoming() }, [fetchUpcoming])

  return (
    <div className="card p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-blue-light" />
        Upcoming IPO Calendar
      </h2>

      {upcomingLoading ? (
        <div className="skeleton h-40" />
      ) : upcoming.length === 0 ? (
        <div className="text-text-muted text-sm">No upcoming IPOs found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-pro">
            <thead>
              <tr>
                <th>Company</th>
                <th>Symbol</th>
                <th>Exchange</th>
                <th>Date</th>
                <th className="!text-right">Days</th>
                <th className="!text-right">Price Range</th>
                <th className="!text-right">Shares</th>
                <th className="!text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map((ipo, i) => (
                <tr
                  key={`${ipo.symbol}-${i}`}
                >
                  <td className="py-2 pr-4 text-text-primary font-medium">{ipo.name}</td>
                  <td className="py-2 pr-4 font-mono font-semibold text-blue-light">{ipo.symbol}</td>
                  <td className="py-2 pr-4 text-text-muted">{ipo.exchange}</td>
                  <td className="py-2 pr-4 text-text-muted">{formatDate(ipo.date)}</td>
                  <td className="py-2 pr-4 text-right text-text-muted font-mono">
                    {ipo.days_until > 0 ? `${ipo.days_until}d` : 'Today'}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-text-primary">{ipo.price_range}</td>
                  <td className="py-2 pr-4 text-right text-text-muted font-mono">
                    {ipo.shares ? `${(ipo.shares / 1e6).toFixed(1)}M` : 'TBA'}
                  </td>
                  <td className="py-2 text-center">
                    <StatusBadge status={ipo.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Tab: Recent IPOs ──────────────────────────────────────────────────────────

function RecentTab() {
  const { recent, recentLoading, recentAsOf, recentStale, recentNote, fetchRecent } = useIPO()
  const [days, setDays] = useState(30)

  useEffect(() => { fetchRecent(days) }, [fetchRecent, days])

  return (
    <div className="space-y-4">
      {/* Days selector */}
      <div className="glass rounded-xl px-3 py-2 flex items-center gap-2">
        <span className="text-text-muted text-sm">Show:</span>
        {[14, 30, 60, 90].map((d) => (
          <button
            type="button"
            key={d}
            onClick={() => setDays(d)}
            className={clsx(
              'px-3 py-1 rounded text-sm font-medium transition-colors',
              days === d
                ? 'bg-blue text-white'
                : 'bg-surface-card text-text-muted hover:text-text-primary',
            )}
          >
            {d}d
          </button>
        ))}
      </div>

      <div className="card p-4">
        <h2 className="text-lg font-semibold text-text-primary mb-1 flex items-center gap-2 flex-wrap">
          <TrendingUp className="w-5 h-5 text-green" />
          Recent IPO Performance
          {recentAsOf && (
            <span className="text-xs font-normal text-text-muted">
              as of {new Date(recentAsOf).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {recentStale && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-400/15 text-amber-400">STALE</span>
          )}
        </h2>
        {recentNote && (
          <p className="text-xs text-amber-400 mb-2">{recentNote}</p>
        )}
        <div className="mb-3" />

        {recentLoading ? (
          <div className="skeleton h-40" />
        ) : recent.length === 0 ? (
          <div className="text-text-muted text-sm">
            {recentNote ?? `No recent IPOs in the last ${days} days.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-pro">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Symbol</th>
                  <th>Exchange</th>
                  <th>IPO Date</th>
                  <th className="!text-right">IPO Price</th>
                  <th className="!text-right">Current</th>
                  <th className="!text-right">1st Day</th>
                  <th className="!text-right">Total Return</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((ipo, i) => (
                  <tr
                    key={`${ipo.symbol}-${i}`}
                  >
                    <td className="py-2 pr-4 text-text-primary font-medium">{ipo.name}</td>
                    <td className="py-2 pr-4 font-mono font-semibold text-blue-light">{ipo.symbol}</td>
                    <td className="py-2 pr-4 text-text-muted">{ipo.exchange}</td>
                    <td className="py-2 pr-4 text-text-muted">{formatDate(ipo.ipo_date)}</td>
                    <td className="py-2 pr-4 text-right font-mono text-text-primary">
                      {ipo.ipo_price != null ? `$${ipo.ipo_price.toFixed(2)}` : 'N/A'}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-text-primary">
                      {ipo.current_price != null ? `$${ipo.current_price.toFixed(2)}` : 'N/A'}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <ReturnBadge value={ipo.first_day_return_pct} />
                    </td>
                    <td className="py-2 text-right">
                      <ReturnBadge value={ipo.total_return_pct} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Tab: Performance ──────────────────────────────────────────────────────────

function PerformanceTab() {
  const { performance, performanceLoading, fetchPerformance } = useIPO()

  useEffect(() => { fetchPerformance() }, [fetchPerformance])

  return (
    <div className="space-y-4">
      {performanceLoading ? (
        <div className="skeleton h-40" />
      ) : !performance ? (
        <div className="text-text-muted text-sm">No performance data available.</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-children">
            <div className="card p-3">
              <div className="text-text-muted text-xs mb-1">Total IPOs</div>
              <div className="text-xl font-bold text-text-primary">{performance.total_ipos}</div>
            </div>
            <div className="card p-3">
              <div className="text-text-muted text-xs mb-1">Positive</div>
              <div className="text-xl font-bold text-green">{performance.positive_performers}</div>
            </div>
            <div className="card p-3">
              <div className="text-text-muted text-xs mb-1">Avg Return</div>
              <div className={clsx(
                'text-xl font-bold',
                performance.avg_return_pct >= 0 ? 'text-green' : 'text-rose-light',
              )}>
                {performance.avg_return_pct >= 0 ? '+' : ''}{performance.avg_return_pct}%
              </div>
            </div>
            <div className="card p-3">
              <div className="text-text-muted text-xs mb-1">Win Rate</div>
              <div className="text-xl font-bold text-text-primary">
                {Math.round(performance.positive_performers / performance.total_ipos * 100)}%
              </div>
            </div>
          </div>

          {/* Top & worst */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {performance.top_performer && (
              <div className="bg-green/5 border border-green/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-green" />
                  <span className="text-xs font-medium text-green">Top Performer</span>
                </div>
                <div className="text-text-primary font-semibold">{performance.top_performer.name}</div>
                <div className="text-text-muted text-xs font-mono">{performance.top_performer.symbol}</div>
                <div className="text-green font-bold font-mono mt-1">
                  +{performance.top_performer.total_return_pct?.toFixed(1)}%
                </div>
              </div>
            )}
            {performance.worst_performer && (
              <div className="bg-rose/5 border border-rose/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-rose-light" />
                  <span className="text-xs font-medium text-rose-light">Worst Performer</span>
                </div>
                <div className="text-text-primary font-semibold">{performance.worst_performer.name}</div>
                <div className="text-text-muted text-xs font-mono">{performance.worst_performer.symbol}</div>
                <div className="text-rose-light font-bold font-mono mt-1">
                  {performance.worst_performer.total_return_pct?.toFixed(1)}%
                </div>
              </div>
            )}
          </div>

          {/* Ranked table */}
          <div className="card p-4">
            <h3 className="text-base font-semibold text-text-primary mb-3">
              {performance.year} IPO Class — Ranked by Return
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-pro">
                <thead>
                  <tr>
                    <th className="!text-right w-10">#</th>
                    <th>Company</th>
                    <th>Symbol</th>
                    <th>IPO Date</th>
                    <th className="!text-right">IPO Price</th>
                    <th className="!text-right">Current</th>
                    <th className="!text-right">1st Day</th>
                    <th className="!text-right">Total Return</th>
                  </tr>
                </thead>
                <tbody>
                  {performance.ipos.map((ipo) => (
                    <tr
                      key={ipo.symbol}
                    >
                      <td className="py-2 pr-3 text-right text-text-muted font-mono">{ipo.rank}</td>
                      <td className="py-2 pr-4 text-text-primary font-medium">{ipo.name}</td>
                      <td className="py-2 pr-4 font-mono font-semibold text-blue-light">{ipo.symbol}</td>
                      <td className="py-2 pr-4 text-text-muted">{formatDate(ipo.ipo_date)}</td>
                      <td className="py-2 pr-4 text-right font-mono text-text-primary">
                        {ipo.ipo_price != null ? `$${ipo.ipo_price.toFixed(2)}` : 'N/A'}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-text-primary">
                        {ipo.current_price != null ? `$${ipo.current_price.toFixed(2)}` : 'N/A'}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <ReturnBadge value={ipo.first_day_return_pct} />
                      </td>
                      <td className="py-2 text-right">
                        <ReturnBadge value={ipo.total_return_pct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ── Tab: Splits ────────────────────────────────────────────────────────────────

function SplitsTab() {
  const { splits, splitsLoading, fetchSplits } = useIPO()

  useEffect(() => { fetchSplits(90) }, [fetchSplits])

  const SplitsTable = ({ items, title }: { items: StockSplit[], title: string }) => (
    <div className="card p-4">
      <h3 className="text-base font-semibold text-text-primary mb-3">{title}</h3>
      {items.length === 0 ? (
        <div className="text-text-muted text-sm">None found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-pro">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Company</th>
                <th>Ratio</th>
                <th>Ex-Date</th>
                <th>Announced</th>
                <th className="!text-center">Type</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s: StockSplit, i: number) => (
                <tr
                  key={`${s.ticker}-${i}`}
                >
                  <td className="py-2 pr-4 font-mono font-semibold text-blue-light">{s.ticker}</td>
                  <td className="py-2 pr-4 text-text-primary">{s.name}</td>
                  <td className="py-2 pr-4 font-mono text-text-primary font-semibold">{s.ratio}</td>
                  <td className="py-2 pr-4 text-text-muted">
                    {s.ex_date ? formatDate(s.ex_date) : 'TBA'}
                    {s.days_until != null && s.days_until >= 0 && (
                      <span className="ml-2 text-xs text-amber-400">
                        {s.days_until === 0 ? 'Today' : `in ${s.days_until}d`}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-text-muted">
                    {s.announced ? formatDate(s.announced) : '—'}
                  </td>
                  <td className="py-2 text-center">
                    <span className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium capitalize',
                      s.type === 'forward'
                        ? 'bg-green/15 text-green'
                        : 'bg-amber-400/15 text-amber-400',
                    )}>
                      {s.type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h2 className="text-lg font-semibold text-text-primary mb-1 flex items-center gap-2">
          <Scissors className="w-5 h-5 text-amber-400" />
          Stock Splits
        </h2>
        <p className="text-text-muted text-xs mb-0">
          Recent and upcoming forward/reverse splits from major companies
        </p>
      </div>

      {splitsLoading ? (
        <div className="skeleton h-40" />
      ) : !splits ? (
        <div className="text-text-muted text-sm">No splits data available.</div>
      ) : (
        <>
          <SplitsTable items={splits.upcoming} title="Upcoming Splits" />
          <SplitsTable items={splits.recent} title="Recent Splits (last 90 days)" />
        </>
      )}
    </div>
  )
}

// ── Tab: Dividends ────────────────────────────────────────────────────────────

function DividendsTab() {
  const { dividends, dividendsLoading, fetchDividends } = useIPO()

  useEffect(() => { fetchDividends(30) }, [fetchDividends])

  return (
    <div className="card p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-green" />
        Upcoming Ex-Dividend Dates
      </h2>
      <p className="text-text-muted text-xs mb-4">
        Holdings and watchlist — next 30 days
      </p>

      {dividendsLoading ? (
        <div className="skeleton h-40" />
      ) : dividends.length === 0 ? (
        <div className="text-text-muted text-sm">No upcoming ex-dividend dates in the next 30 days.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-pro">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Company</th>
                <th>Ex-Date</th>
                <th>Pay Date</th>
                <th className="!text-right">Days</th>
                <th className="!text-right">Amount</th>
                <th className="!text-right">Yield</th>
                <th className="!text-center">Frequency</th>
              </tr>
            </thead>
            <tbody>
              {dividends.map((div, i) => (
                <tr
                  key={`${div.ticker}-${i}`}
                >
                  <td className="py-2 pr-4 font-mono font-semibold text-blue-light">
                    {div.ticker}
                    {div.in_watchlist && (
                      <span className="ml-1 text-[10px] bg-blue/15 text-blue-light px-1 rounded">WL</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-text-primary">{div.name}</td>
                  <td className="py-2 pr-4 text-text-muted">{formatDate(div.ex_date)}</td>
                  <td className="py-2 pr-4 text-text-muted">{formatDate(div.pay_date)}</td>
                  <td className="py-2 pr-4 text-right font-mono text-text-muted">
                    {div.days_until === 0 ? 'Today' : `${div.days_until}d`}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-green font-semibold">
                    £{(div.amount ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-text-primary">
                    {(div.yield_pct ?? 0).toFixed(2)}%
                  </td>
                  <td className="py-2 text-center">
                    <span className="badge-neutral">
                      {div.frequency}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

type Tab = 'upcoming' | 'recent' | 'performance' | 'splits' | 'dividends'

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'upcoming',    label: 'Upcoming IPOs',  icon: CalendarDays },
  { id: 'recent',      label: 'Recent IPOs',    icon: TrendingUp },
  { id: 'performance', label: 'Performance',    icon: Trophy },
  { id: 'splits',      label: 'Splits',         icon: Scissors },
  { id: 'dividends',   label: 'Dividends',      icon: DollarSign },
]

export default function IPOPage() {
  const [activeTab, setActiveTab] = useState<Tab>('upcoming')

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Rocket className="w-7 h-7 text-blue-light" />
        <div>
          <h1 className="page-title">IPO & Corporate Actions</h1>
          <p className="text-text-muted text-sm">
            Track upcoming IPOs, recent listings, stock splits, and dividend ex-dates
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass rounded-xl p-1 flex gap-1 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            type="button"
            key={id}
            onClick={() => setActiveTab(id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap',
              activeTab === id
                ? 'bg-blue/15 text-blue-light'
                : 'text-text-muted hover:text-text-primary',
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'upcoming'    && <UpcomingTab />}
        {activeTab === 'recent'      && <RecentTab />}
        {activeTab === 'performance' && <PerformanceTab />}
        {activeTab === 'splits'      && <SplitsTab />}
        {activeTab === 'dividends'   && <DividendsTab />}
      </div>
    </div>
  )
}
